import React, { useEffect, useRef, useState } from 'react';
import { Input, InputCheckRules, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { Form } from '@components/atoms/Form';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@hooks/useNotifications';
import { useResetPassword } from '@services/useResetPassword';
import { useNavigate } from 'react-router-dom';
import { SITE_URLS } from '@constants';

interface ResetPasswordFormProps {
    token: string;
}

interface Errors {
    password: 'valid' | 'invalid' | null;
    confirmation: string[];
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ token }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showDefaultNotification } = useNotifications();

    const formRef = useRef<HTMLFormElement>(null);

    const { request, loading, error, success, lastResponseTime } = useResetPassword();

    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [validPassword, setValidPassword] = useState<boolean>(false);

    const [errors, setErrors] = useState<Errors>({
        password: null,
        confirmation: [],
    });

    const translations = {
        title: t('recover.reset.title'),
        passwordLabel: t('password-form.password.label'),
        confirmPasswordLabel: t('password-form.confirm-password.label'),
        submit: t('recover.reset.submit'),
        mismatch: t('recover.reset.mismatch'),
    };

    const validate = (): Errors => {
        const newErrors: Errors = {
            password: validPassword ? 'valid' : 'invalid',
            confirmation: [],
        };

        if (password !== confirmation) {
            newErrors.confirmation.push(translations.mismatch);
        }

        setErrors(newErrors);
        return newErrors;
    };

    const handleSubmit = () => {
        if (loading) return;

        const errs = validate();
        if (errs.password !== 'valid' || errs.confirmation.length > 0) return;

        request({
            token,
            password,
            password_confirmation: confirmation,
        });
    };

    useEffect(() => {
        if (!lastResponseTime) return;

        if (success) {
            showDefaultNotification({ type: 'success', message: success });
            navigate(`/${SITE_URLS.login}`);
        }

        if (error) {
            showDefaultNotification({ type: 'error', message: error });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <Form formRef={formRef} classes="d-flex flex-column w-100" onEnterPress={handleSubmit} onSubmit={handleSubmit}>
            <Typography fontSize="24" fontWeight="800" align="center">
                {translations.title}
            </Typography>

            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <Input
                type={InputTypes.password}
                variant={InputVariants.labeledInput}
                label={`${translations.passwordLabel}*`}
                value={password}
                onChange={(value) => setPassword(value)}
                state={errors.password === 'invalid' ? InputStates.error : undefined}
                checkRules={InputCheckRules.passwordBuild}
                onValidationChange={setValidPassword}
            />

            <Space spaceBreakpoints={{ xs: { space: 15 } }} />

            <Input
                type={InputTypes.password}
                variant={InputVariants.labeledInput}
                label={`${translations.confirmPasswordLabel}*`}
                value={confirmation}
                onChange={(value) => setConfirmation(value)}
                state={errors.confirmation.length > 0 ? InputStates.error : undefined}
                errorMessages={errors.confirmation}
            />

            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <Button label={translations.submit} mode={ButtonMode.primary} onClick={handleSubmit} disabled={loading} />
        </Form>
    );
};
